import {
    ChangeDetectionStrategy, Component, inject
} from "@angular/core";
import {
    MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle
} from "@angular/material/dialog";
import { MatButton } from "@angular/material/button";
import { MimeType } from "@shopliftr/common-js/data";

import { ImageCropperComponent } from "../../components/shared/image-cropper/image-cropper.component";
import { ICroppedImage } from "../../components/shared/image-cropper/interface/cropped-image";
import { DialogContentContainerDirective } from "../../directives/dialog-content-container/dialog-content-container.directive";
import { ButtonDirective } from "../../application/directives/button/button.directive";
import { DialogCloseButtonDirective } from "../../application/directives/button/dialog-close-button.directive";
import { IImageCropperDialogData } from "./interface/image-cropper-dialog-data";
import { IImageCropperDialogResult } from "./interface/image-cropper-dialog-result";

/**
 * Dialog that wraps {@link ImageCropperComponent} and nothing else — no upload,
 * no form, no API calls.
 *
 * Closes with `{ action: "crop", image }` when the user confirms,
 * `{ action: "remove" }` when they clear the image, and `undefined` on cancel.
 */
@Component({
    selector: "mgd-image-cropper-dialog",
    templateUrl: "./image-cropper-dialog.component.html",
    styleUrl: "./image-cropper-dialog.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        DialogContentContainerDirective,
        ImageCropperComponent,
        ButtonDirective,
        DialogCloseButtonDirective,
        MatButton
    ]
})
export class ImageCropperDialogComponent {

    readonly dialogRef
        = inject<MatDialogRef<ImageCropperDialogComponent, IImageCropperDialogResult>>(MatDialogRef);

    readonly data = inject<IImageCropperDialogData>(MAT_DIALOG_DATA);

    /**
     * Crop parameters passed down to `mgd-image-cropper` as its initial state.
     * Held as plain mutable fields, not signals, to match that component's own
     * `@Input()` style; `mgd-image-cropper` only ever mutates its own local
     * copies (e.g. from its lock button) and never reports them back up.
     */
    internalCropWidth: number;

    internalCropHeight: number;

    lockDimensions: boolean;

    hideSetDimensions: boolean;

    readonly mimeType: MimeType;

    /**
     * The most recent crop. The cropper emits continuously, so nothing is
     * committed until the user confirms.
     */
    private _latchedCrop?: ICroppedImage;

    constructor() {

        const hasRequiredSize = !!this.data.width && !!this.data.height;

        this.internalCropWidth = this.data.width;
        this.internalCropHeight = this.data.height;
        this.lockDimensions = hasRequiredSize;
        this.hideSetDimensions = hasRequiredSize;
        this.mimeType = this.data.mimeType ?? MimeType.Png;
    }

    /**
     * Latches the most recent crop without committing it.
     *
     * @param cropped - The cropper's current output
     */
    onCrop(cropped: ICroppedImage): void {

        this._latchedCrop = cropped;
    }

    /**
     * Commits the latched crop and closes.
     *
     * The embedded cropper only emits after cropperjs initializes and the
     * image decodes — hundreds of ms, and it may never happen at all if the
     * image fails to decode — so there is a window, or a permanent state,
     * where nothing has been latched yet. Closing in that window would hand
     * the host a `"crop"` result with no image, so this is a no-op until
     * something has actually been latched.
     */
    onCropClick(): void {

        if (!this._latchedCrop) {
            return;
        }

        this.dialogRef.close({
            action: "crop",
            image: this._latchedCrop
        });
    }

    /**
     * Closes asking the host to clear the image.
     */
    onRemoveClick(): void {

        this.dialogRef.close({ action: "remove" });
    }
}
